import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Menu, X, User, LogOut, LayoutDashboard, PenTool } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { JabalipuraanLogo } from '../common/JabalipuraanLogo';

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/articles', label: 'Articles' },
  { href: '/initiatives', label: 'Initiatives' },
  { href: '/media', label: 'Media' },
  { href: '/contact', label: 'Contact' },
];

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, isAdmin, isAuthor } = useAuth();
  const location = useLocation();

  // Capture current page so user is returned here after Google login
  const rememberCurrentPage = () => {
    const path = location.pathname + location.search;
    if (
      path &&
      !path.startsWith('/login') &&
      !path.startsWith('/register') &&
      !path.startsWith('/auth/callback')
    ) {
      sessionStorage.setItem('postLoginRedirect', path);
    }
  };

  // Authors and admins can access author panel
  const canAccessAuthorPanel = isAuthor || isAdmin;

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <nav className="frosted-nav fixed top-0 left-0 right-0 z-50 border-b border-border/50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <JabalipuraanLogo 
            size="md" 
            showText={true}
            textClassName="text-foreground hidden sm:block"
            linkTo="/"
          />

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`nav-link text-sm font-medium tracking-wide uppercase ${
                  isActive(link.href) 
                    ? 'text-foreground active' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Auth Section */}
          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                    <User className="h-4 w-4" />
                    <span className="font-medium">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2" data-testid="admin-link">
                        <LayoutDashboard className="h-4 w-4" />
                        Admin Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {canAccessAuthorPanel && (
                    <DropdownMenuItem asChild>
                      <Link to="/author" className="flex items-center gap-2" data-testid="author-link">
                        <PenTool className="h-4 w-4" />
                        Author Panel
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {(isAdmin || canAccessAuthorPanel) && <DropdownMenuSeparator />}
                  <DropdownMenuItem onClick={logout} className="flex items-center gap-2 text-destructive" data-testid="logout-btn">
                    <LogOut className="h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login" onClick={rememberCurrentPage}>
                  <Button variant="ghost" className="font-medium" data-testid="login-btn">
                    Login
                  </Button>
                </Link>
                <Link to="/register" onClick={rememberCurrentPage}>
                  <Button className="font-medium" data-testid="register-btn">
                    Register
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-border/50 animate-fade-in">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2 text-sm font-medium uppercase tracking-wide ${
                    isActive(link.href) 
                      ? 'text-foreground bg-muted' 
                      : 'text-muted-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="border-t border-border/50 mt-2 pt-4 px-4">
                {user ? (
                  <div className="flex flex-col gap-2">
                    <span className="text-sm text-muted-foreground">Signed in as {user.name}</span>
                    {isAdmin && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 py-2 text-sm font-medium"
                      >
                        <LayoutDashboard className="h-4 w-4" />
                        Admin Panel
                      </Link>
                    )}
                    {canAccessAuthorPanel && (
                      <Link
                        to="/author"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center gap-2 py-2 text-sm font-medium"
                      >
                        <PenTool className="h-4 w-4" />
                        Author Panel
                      </Link>
                    )}
                    <button
                      onClick={() => { logout(); setMobileMenuOpen(false); }}
                      className="flex items-center gap-2 py-2 text-sm font-medium text-destructive"
                    >
                      <LogOut className="h-4 w-4" />
                      Logout
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-3">
                    <Link to="/login" onClick={() => { rememberCurrentPage(); setMobileMenuOpen(false); }}>
                      <Button variant="outline" size="sm">Login</Button>
                    </Link>
                    <Link to="/register" onClick={() => { rememberCurrentPage(); setMobileMenuOpen(false); }}>
                      <Button size="sm">Register</Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
